import { useIsSignedIn } from '@sunucupark/client-react';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  stringifySearchWith,
  useNavigate,
  useRouterState,
  Outlet,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { ComposerHost } from '@/features/compose';
import type { WebClient } from './client';
import { NewMailNotifier } from './NewMailNotifier';
import { SessionWatcher } from './SessionWatcher';
import { SignInScreen } from './screens/SignInScreen';
import { AppearanceBridge } from './AppearanceBridge';
import { ComposerSettingsBridge } from './ComposerSettingsBridge';
import { ShortcutBridge } from './ShortcutBridge';

/*
 * The rail arrives with the first signed-in page, beside it rather than before
 * it: its account menu is the only menu a first visit would otherwise download
 * up front (ADR 0006, bundle budget). Until then its column is held empty, so
 * nothing moves when it lands.
 */
const AppRail = lazy(() => import('./chrome/AppRail').then((module) => ({ default: module.AppRail })));

export interface RouterContext {
  client: WebClient;
  /** The mailbox's inbox id, resolved from the folder list (and cached with it). */
  inboxFolderId: () => Promise<string>;
}

/** Whether the list shows threads or single messages; the reader follows it. */
export type MailView = 'conversation' | 'message';

export interface MailSearch {
  /** The row the reader is showing. Absent means the reader is closed. */
  selected?: string;
  view: MailView;
  /** What the search box holds; absent means the folder is shown. */
  q?: string;
  /** Only the mail that has not been read yet. */
  unread?: boolean;
}

/**
 * Everything that reacts to the keyboard or to new mail has to sit inside the
 * router, because it navigates; the rest of the wiring lives in `Providers`.
 */
function RootLayout() {
  const navigate = useNavigate();
  const signedIn = useIsSignedIn();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Every signed-in page stands beside the rail; the sign-in page and a composer popped out into
  // its own window stand alone.
  const withRail = signedIn && !pathname.startsWith('/sign-in') && !pathname.startsWith('/compose/');
  return (
    <>
      {withRail ? (
        <div className="flex h-dvh min-h-0 bg-background">
          <Suspense fallback={<div aria-hidden className="w-16 shrink-0" />}>
            <AppRail />
          </Suspense>
          {/*
            The page scrolls here and nowhere else. `relative` makes this box the containing block of
            everything absolutely placed inside it — the screen-reader-only labels and radio inputs
            among them — so none of it escapes its clip and stretches the document under the rail;
            `overscroll-contain` keeps the wheel from carrying on to the window at the end.
          */}
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-auto overscroll-contain">
            <Outlet />
          </div>
        </div>
      ) : (
        <Outlet />
      )}
      {/*
        The composers live above the routes, so moving between folders — or to
        the settings screen and back — never unmounts a half-written message.
        They draw and download nothing until one is open.
      */}
      <ComposerHost
        onPopOut={(draftId) => void navigate({ to: '/compose/$draftId', params: { draftId } })}
      />
      <AppearanceBridge />
      <ComposerSettingsBridge />
      <ShortcutBridge />
      <NewMailNotifier />
      <SessionWatcher />
    </>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

/** Sends a signed-out visitor to the sign-in page, remembering where they wanted to go. */
function requireSession(context: RouterContext, pathname: string): void {
  if (!context.client.session.snapshot()) throw redirect({ to: '/sign-in', search: { next: pathname } });
}

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  // Where to go after signing in; only in-app paths are accepted, and the check is
  // hand-written so the router does not pull a validator into the first download.
  validateSearch: (search: Record<string, unknown>): { next?: string } => {
    const next = search.next;
    // One leading slash and nothing a browser could read as another host: no `//`, no `/\`, no control characters.
    return typeof next === 'string' && /^\/(?![/\\])[^\\\s]*$/.test(next) ? { next } : {};
  },
  beforeLoad: ({ context, search }) => {
    if (context.client.session.snapshot()) throw redirect({ to: search.next ?? '/' });
  },
  component: SignInScreen,
});

/** `/` opens the inbox; its id comes from the server, never from a constant. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context, location }) => {
    requireSession(context, location.pathname);
    throw redirect({
      to: '/mail/$folderId',
      params: { folderId: await context.inboxFolderId() },
      search: { view: 'conversation' },
    });
  },
});

const mailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mail/$folderId',
  validateSearch: (search: Record<string, unknown>): MailSearch => {
    // The router's query decoder turns every all-digit value into a number
    // before any parser runs, and mailbox ids are all digits — so the id is
    // coerced back to text here, once, where the shape of the search is decided.
    const raw = search.selected;
    const selected = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : undefined;
    const view: MailView = search.view === 'message' ? 'message' : 'conversation';
    const rawQuery = search.q;
    const q = typeof rawQuery === 'string' || typeof rawQuery === 'number' ? String(rawQuery) : undefined;
    const unread = search.unread === true || search.unread === 'true';
    return {
      ...(selected === undefined ? {} : { selected }),
      ...(q ? { q } : {}),
      ...(unread ? { unread: true } : {}),
      view,
    };
  },
  beforeLoad: ({ context, location }) => {
    requireSession(context, location.pathname);
  },
  // The mailbox is its own chunk: a visitor on the sign-in page never downloads it.
  component: lazyRouteComponent(() => import('./screens/MailShell'), 'MailShell'),
});

/** The settings: a menu of pages, each its own address (`/settings/mail`…), in a chunk of their own. */
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: ({ context, location }) => {
    requireSession(context, location.pathname);
  },
  component: lazyRouteComponent(() => import('./screens/settings'), 'SettingsLayout'),
});

/**
 * `/settings` opens the first page — or, for an old link to a card of the single
 * page that came before (`/settings#settings-signatures`), the page that card is on.
 */
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: async ({ location }) => {
    const card = location.hash.replace(/^#?settings-/, '');
    // Only a link with a card in it waits for the map, which comes with the settings' own chunk.
    const page = card ? (await import('./screens/settings')).PAGE_OF_CARD[card] : undefined;
    if (page) throw redirect({ to: page, hash: `settings-${card}` });
    throw redirect({ to: '/settings/general' });
  },
});

const settingsGeneralRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'general',
  component: lazyRouteComponent(() => import('./screens/settings'), 'GeneralSettings'),
});

const settingsMailRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'mail',
  component: lazyRouteComponent(() => import('./screens/settings'), 'MailSettings'),
});

const settingsAutoReplyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'auto-reply',
  component: lazyRouteComponent(() => import('./screens/settings'), 'AutoReplySettings'),
});

const settingsFiltersRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'filters',
  component: lazyRouteComponent(() => import('./screens/settings'), 'FilterSettings'),
});

const settingsAccountRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'account',
  component: lazyRouteComponent(() => import('./screens/settings'), 'AccountSettings'),
});

const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  beforeLoad: ({ context, location }) => {
    requireSession(context, location.pathname);
  },
  // Its own chunk: a reader who never opens the task list never downloads it.
  component: lazyRouteComponent(() => import('./screens/TasksScreen'), 'TasksScreen'),
});

const contactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts',
  beforeLoad: ({ context, location }) => {
    requireSession(context, location.pathname);
  },
  // Its own chunk, like the task list: the mailbox does not pay for it.
  component: lazyRouteComponent(() => import('./screens/ContactsScreen'), 'ContactsScreen'),
});

const composeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compose/$draftId',
  beforeLoad: ({ context, location }) => {
    requireSession(context, location.pathname);
  },
  // Its own chunk, and it brings the mailbox strings the composer needs.
  component: lazyRouteComponent(() => import('./screens/ComposeScreen'), 'ComposeScreen'),
});

export const routeTree = rootRoute.addChildren([
  signInRoute,
  indexRoute,
  mailRoute,
  composeRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    settingsGeneralRoute,
    settingsMailRoute,
    settingsAutoReplyRoute,
    settingsFiltersRoute,
    settingsAccountRoute,
  ]),
  tasksRoute,
  contactsRoute,
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    /*
     * Search parameters are written as plain text: the default codec is JSON,
     * which would write an id back as `%22688%22`. Reading them plain is not
     * possible — the router's decoder coerces `688` to a number before any
     * parser sees it — so each route's `validateSearch` states the type it wants.
     */
    stringifySearch: stringifySearchWith(String),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
